import { BaseResponseSchema, getRequiredMessage } from '@monetize/types/common';
import { z } from 'zod';
import { ContactRespSchema } from './contact.types';
import { SigningOrderEnumZ } from './enums.types';
import { ESignRecipient } from './esign.types';
import { StorageSchema } from './storage.types';

export enum AccountDocumentStatusEnum {
  CREATED = 'CREATED',
  SENT = 'SENT',
  SIGNED = 'SIGNED',
}
export const AccountDocumentStatusEnumZ = z.enum(AccountDocumentStatusEnum);

export const AccountDocumentDetailResponseSchema = BaseResponseSchema.extend({
  accountId: z.string(),
  accountName: z.string(),
  signingOrder: SigningOrderEnumZ,
  name: z.string(),
  description: z.string().optional(),
  storageObjectId: z.string(),
  esignEnvelopeId: z.string().optional(),
  signedDocumentStorageObjectId: z.string().optional(),
  contacts: z.object({
    primaryESignContacts: z.array(ContactRespSchema),
    secondaryESignContacts: z.array(ContactRespSchema),
    additionalESignRecipients: z.array(ContactRespSchema),
  }),
  storageObject: StorageSchema,
  signedDocumentStorageObject: StorageSchema.optional(),

  createdBy: z.string(),
  createDate: z.string(),
  lastModifiedBy: z.string(),
  modifyDate: z.string(),
  status: AccountDocumentStatusEnumZ,
  signedAt: z.string().optional(),
  sentAt: z.string().optional(),
});
export type AccountDocumentDetailResponse = z.infer<
  typeof AccountDocumentDetailResponseSchema
>;

export const AccountDocumentBasicResponseSchema = BaseResponseSchema.extend({
  accountId: z.string(),
  accountName: z.string(),
  signingOrder: SigningOrderEnumZ,
  name: z.string(),
  description: z.string().optional(),
  storageObjectId: z.string(),
  esignEnvelopeId: z.string().optional(),
  signedDocumentStorageObjectId: z.string().optional(),
  storageObject: StorageSchema,
  signedDocumentStorageObject: StorageSchema.optional(),
  status: AccountDocumentStatusEnumZ,
  signedAt: z.string().optional(),
  sentAt: z.string().optional(),
});
export type AccountDocumentBasicResponse = z.infer<
  typeof AccountDocumentBasicResponseSchema
>;

export const AccountDocumentFormSchema = z.object({
  description: z
    .string()
    .max(255, 'Description must be less than 255 characters')
    .nullish()
    .transform((val) => (val ? val.trim() : val)),
  name: z
    .string()
    .nonempty(getRequiredMessage('Name'))
    .max(255, 'Name must be less than 255 characters')
    .transform((val) => (val ? val.trim() : val)),
  signingOrder: SigningOrderEnumZ.nullish(),
});
export type AccountDocumentForm = z.infer<typeof AccountDocumentFormSchema>;

export const ACCOUNT_DOCUMENT_STATUS_DISPLAY = {
  [AccountDocumentStatusEnum.CREATED]: 'Created',
  [AccountDocumentStatusEnum.SENT]: 'Sent',
  [AccountDocumentStatusEnum.SIGNED]: 'Signed',
};

export const AccountDocumentContactFormSchema = z.object({
  primaryInternalId: z
    .string()
    .nonempty(getRequiredMessage('Primary Internal Contact')),
  primaryExternalId: z
    .string()
    .nonempty(getRequiredMessage('Primary External Contact')),
  esignCcIds: z.array(z.string()),
});
export type AccountDocumentContactForm = z.infer<
  typeof AccountDocumentContactFormSchema
>;

export type AccountDocumentSignerStatsResponse = {
  recipientCount: string; // example: "3"
  currentRoutingOrder: string; // example: "3"
  signers: ESignRecipient[];
  ccContacts: ESignRecipient[];
};
